import AddIcon from "@mui/icons-material/Add";
import {
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
} from "@mui/material";
import axios from "axios";
import cx from "classnames";
import { debounce, some } from "lodash";
import React, {
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as LayersIcon } from "assets/layers.svg";
import { ReactComponent as List } from "assets/list.svg";
import { ReactComponent as RenameIcon } from "assets/pencil-icon.svg";
import { ReactComponent as Preview } from "assets/preview.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import Guide from "components/Guide";
import Layer from "components/Layers/Layer";
import LayerCascade from "components/Layers/LayerCascade";
import SmartVectorsOutputGuide from "components/Layers/SmartVectorsOutputGuide";
import TrackingOutputGuide from "components/Layers/TrackingOutputGuide";
import InferencingBackdrop from "components/Services/InferencingBackdrop";
import SmartVectorsGenerate from "components/Services/SmartVectors/SmartVectorsGenerate";
import ShotContext from "components/Shot/ShotContext";
import ThrottlePopup from "components/ThrottlePopup";
import TrackingSidePanel from "components/TrackingSidepanel";
import {
  UserGuides,
  SERVICES,
  STATUSES,
  ViewModes,
  WINDOW_EVENTS,
} from "constants/constants";
import { SLAPSHOT_HOME_URL } from "constants/urls";
import { LayerCompact } from "models/Layer";
import { getCutouts } from "services/CutoutService";
import {
  createLayer,
  deleteLayer,
  getLayerDetail,
  getLayerReview,
  getLayers,
} from "services/LayerService";
import {
  canRunInference,
  getShotDetail,
  runInference,
} from "services/ShotService";
import {
  ACTION_SUBSCRIBE_CUTOUT_UPDATE,
  ACTION_SUBSCRIBE_INFERENCE_UPDATE,
  getRequestData,
  getSocket,
  closeSocket,
} from "services/WebSocketService";
import { formatDate } from "utils/helper";
import { useCountdown } from "utils/hooks";

import styles from "./Layers.module.scss";
import TrackingLayer from "./TrackingLayer";

const SERVICE_HEADERS = {
  [SERVICES.ROTOSCOPING]: "Layers",
  [SERVICES.RETIME]: "Retimed Shots",
  [SERVICES.DEPTH_MAP]: "Depth Maps",
  [SERVICES.TRACKING]: "Keyframe",
  [SERVICES.SMART_VECTORS]: "Motion Vectors",
};

const SERVICE_GUIDE = {
  [SERVICES.ROTOSCOPING]: UserGuides.FirstLayer,
  [SERVICES.RETIME]: UserGuides.FirstRetime,
  [SERVICES.DEPTH_MAP]: UserGuides.FirstDepthMap,
  [SERVICES.SMART_VECTORS]: UserGuides.FirstSmartVectors,
};

const SERVICE_GUIDE_TITLE = {
  [SERVICES.ROTOSCOPING]: "You added your first layer",
  [SERVICES.RETIME]: "You added your first retimed video",
  [SERVICES.DEPTH_MAP]: "You created your first depth map",
  [SERVICES.SMART_VECTORS]: "Your Motion Vectors output is ready",
};

const BUTTON_TEXT: Partial<Record<SERVICES, string>> = {
  [SERVICES.ROTOSCOPING]: "New Layer",
  [SERVICES.RETIME]: "Add More",
  [SERVICES.TRACKING]: "New Keyframe",
};

const SERVICE_HINT = {
  [SERVICES.ROTOSCOPING]:
    "To start, click the Add Annotation button and then select any object in the video.",
  [SERVICES.RETIME]: "To start, click the Add Retime Length button.",
  [SERVICES.DEPTH_MAP]: "To start, click on the Create Depth Map button.",
  [SERVICES.TRACKING]: "To start, click the Track Camera button.",
  [SERVICES.SMART_VECTORS]:
    "To start, click + Add Region of Interest to pick a frame and draw a region, then Generate Motion Vectors in the panel below when ready.",
};
export interface LayersRef {
  onInferenceLayerUpdate: (id: number) => void;
  refreshCutouts: () => void;
}

export const AiIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="aiIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#6565ec" }} />
          <stop offset="18.5%" style={{ stopColor: "#009efa" }} />
          <stop offset="39%" style={{ stopColor: "#00c9a7" }} />
          <stop offset="59%" style={{ stopColor: "#ffc75f" }} />
          <stop offset="79.5%" style={{ stopColor: "#ff8066" }} />
          <stop offset="100%" style={{ stopColor: "#d65db1" }} />
        </linearGradient>
      </defs>
      <g clipPath="url(#clip0_1692_12226)">
        <path
          d="M12.6667 5.99959L13.5 4.16626L15.3333 3.33293L13.5 2.49959L12.6667 0.66626L11.8333 2.49959L10 3.33293L11.8333 4.16626L12.6667 5.99959Z"
          className={styles.aiIcon}
        />
        <path
          d="M12.6667 9.99927L11.8333 11.8326L10 12.6659L11.8333 13.4993L12.6667 15.3326L13.5 13.4993L15.3333 12.6659L13.5 11.8326L12.6667 9.99927Z"
          className={styles.aiIcon}
        />
        <path
          d="M7.66602 6.33268L5.99935 2.66602L4.33268 6.33268L0.666016 7.99935L4.33268 9.66602L5.99935 13.3327L7.66602 9.66602L11.3327 7.99935L7.66602 6.33268ZM6.65935 8.65935L5.99935 10.1127L5.33935 8.65935L3.88602 7.99935L5.33935 7.33935L5.99935 5.88602L6.65935 7.33935L8.11268 7.99935L6.65935 8.65935Z"
          className={styles.aiIcon}
        />
      </g>
      <defs>
        <clipPath id="clip0_1692_12226">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const Layers = ({ ref }: { ref?: React.Ref<LayersRef> }) => {
  const {
    isAnnotating,
    layerId,
    selectedLayer,
    setSelectedLayer,
    setCutoutInProgress,
    setLayers,
    layers,
    shotCutouts,
    setShotCutouts,
    selectedShot,
    project,
    setShots,
    clearAnnotationData,
    setReviewLayer,
    setCutouts,
    setLayersForExport,
    service,
    selectedFrame,
    showTrackingPanel,
    setShowTrackingPanel,
    throttleUsage,
    getThrottleUsage,
    setSmartVectorsReviewPane,
  } = useContext(ShotContext);

  const [layerAnchorEl, setLayerAnchorEl] = useState<null | HTMLElement>(null);
  const [showRenameLayerModal, setShowRenameLayerModal] = useState(false);
  const [showLayerDeleteModal, setShowLayerDeleteModal] = useState(false);
  const layerInputEl = useRef<HTMLInputElement | null>(null);
  const [layersViewMode, setLayersViewMode] = useState<ViewModes>(
    ViewModes.Preview,
  );
  const [newCutouts, setNewCutouts] = useState<number[]>([]);
  const [isInferencing, setIsInferencing] = useState(false);

  const [trackingLayers, setTrackingLayers] = useState<
    Record<string, LayerCompact[]>
  >({});
  const [isTracking, setIsTracking] = useState(false);
  const [collapsedLayerGroups, setCollapsedLayerGroups] = useState<
    Record<string, boolean>
  >({});
  const [expandedLayerId, setExpandedLayerId] = useState<number | null>(null);
  const [showThrottleDialog, setShowThrottleDialog] = useState(false);

  const throttledTill = useMemo(() => {
    if (
      throttleUsage?.is_inference_throttled &&
      (throttleUsage.allowed_inferences || 0) < 1 &&
      throttleUsage.next_inference_provision_at
    ) {
      return new Date(throttleUsage.next_inference_provision_at);
    }
    return null;
  }, [throttleUsage]);
  const throttleCountdown = useCountdown(throttledTill, getThrottleUsage);

  const navigate = useNavigate();
  const toggleViewMode = (
    mode: ViewModes,
    setViewMode: React.Dispatch<SetStateAction<ViewModes>>,
  ) => {
    setViewMode(ViewModes[mode]);
  };

  const handleNewLayer = async () => {
    if (!selectedShot) return;
    if (
      service === SERVICES.ROTOSCOPING ||
      service === SERVICES.TRACKING ||
      service === SERVICES.SMART_VECTORS
    ) {
      const layer = await createLayer(selectedShot.id, service, selectedFrame);
      setLayers([layer, ...(layers || [])]);
      setSelectedLayer(layer);
      setCutoutInProgress(null);
      if (!isAnnotating && project)
        navigate(
          `/projects/${project.id}/${service}/shots/${selectedShot.id}/layers/${layer.id}`,
        );
    } else {
      window.dispatchEvent(new Event("showRetimeModal"));
    }
  };

  const handleGetCutouts = useCallback(
    (id: number) => {
      return getCutouts(id).then((result) => {
        let totalVersions = result.filter(
          (v) => v.inference?.prediction_url,
        ).length;

        setCutouts(
          result.map((c) => {
            if (c.inference?.prediction_url) {
              c.version = totalVersions;
              totalVersions--;
            }
            return c;
          }),
        );
        return result;
      });
    },
    [setCutouts],
  );

  const handleGetReviewLayer = useCallback(
    async (id: number) => {
      let ip;
      await axios
        .get("https://api.ipify.org/?format=json")
        .then((res: { data: { ip: string } }) => {
          ip = res.data.ip;
        })
        .catch(() => {});
      getLayerReview(id, ip).then((result) => {
        setReviewLayer(result);
      });
    },
    [setReviewLayer],
  );

  const handleInferenceLayerUpdates = useCallback(
    async (updatedLayerId) => {
      if (layers?.findIndex((l) => l.id === updatedLayerId) === -1) return;

      getLayerDetail(updatedLayerId).then((layer) => {
        setLayers((oldLayers) =>
          [layer, ...(oldLayers?.filter((l) => l.id !== layer.id) || [])].sort(
            (l1, l2) => l2.id - l1.id,
          ),
        );
      });
      if (selectedShot)
        getCutouts(undefined, selectedShot.id, service).then((result) => {
          setShotCutouts(result);
        });

      if (!(layerId && +layerId === updatedLayerId)) return;

      handleGetCutouts(+layerId);
      handleGetReviewLayer(+layerId);
    },
    [
      handleGetCutouts,
      handleGetReviewLayer,
      layerId,
      layers,
      selectedShot,
      service,
      setLayers,
      setShotCutouts,
    ],
  );

  React.useImperativeHandle(ref, () => ({
    onInferenceLayerUpdate(id) {
      handleInferenceLayerUpdates(id);
    },
    refreshCutouts() {
      if (layerId) handleGetCutouts(+layerId);
    },
  }));

  const handleRunInference = async (cutoutsToInference: number[]) => {
    if (!selectedShot) return;

    setNewCutouts([]);
    setIsInferencing(true);

    if (isAnnotating) {
      clearAnnotationData();
      setSelectedLayer(null);
      if (layerId) handleGetCutouts(+layerId);
    }

    runInference(selectedShot.id, cutoutsToInference)
      .then(() => {
        getLayers(selectedShot.id, service).then((result) => {
          setLayers(result);
        });
        getCutouts(undefined, selectedShot.id, service).then((result) => {
          setShotCutouts(result);
        });
        getShotDetail(selectedShot.id).then((shot) => {
          setShots((oldShots) => {
            const newShots = { ...oldShots };
            const key = formatDate(shot.created);
            const shotIndex = newShots[key].findIndex((s) => s.id === shot.id);
            if (shotIndex !== -1) {
              newShots[key][shotIndex] = shot;
              return newShots;
            }
            return oldShots;
          });
        });
        getThrottleUsage();
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        setTimeout(() => {
          setIsInferencing(false);
        }, 6000);
      });
  };

  const handleCanRunInference = useCallback(() => {
    if (!selectedShot) return;
    canRunInference(selectedShot.id)
      .then((res) => {
        setNewCutouts(res.cutouts);
      })
      .catch((error) => toast.error(error.message));
  }, [selectedShot]);

  const refetchLayers = useMemo(
    () =>
      debounce((shotId: number, s: SERVICES) => {
        getLayers(shotId, s).then((l) => setLayers(l));
      }, 500),
    [setLayers],
  );

  const handleViewedInference = useCallback(
    (layer: LayerCompact) => {
      if (!layer.has_viewed_latest_inference === false) return;
      setLayers((oldLayers) =>
        oldLayers?.map((l) => {
          if (l.id === layer.id) {
            l.has_viewed_latest_inference = true;
          }
          return l;
        }),
      );
    },
    [setLayers],
  );

  useEffect(() => {
    if (showRenameLayerModal && layerInputEl.current)
      layerInputEl.current.focus();
  }, [showRenameLayerModal]);

  // Subscribing to cutout updates
  const layersRef = useRef(layers);
  const serviceRef = useRef(service);

  useEffect(() => {
    layersRef.current = layers;
    serviceRef.current = service;
  }, [layers, service, selectedShot]);

  useEffect(() => {
    if (!selectedShot) return () => {};

    const socket = getSocket("cutout");
    if (!socket) {
      return () => {};
    }

    socket.onopen = () => {
      socket.send(
        getRequestData(ACTION_SUBSCRIBE_CUTOUT_UPDATE, {
          shot_id: selectedShot.id,
        }),
      );
    };

    socket.onmessage = (msg) => {
      const latestLayers = layersRef.current;
      const latestService = serviceRef.current;

      if (
        latestLayers &&
        latestLayers.length > 0 &&
        latestLayers.some((l) => l.is_processing_masks === true)
      ) {
        refetchLayers(selectedShot.id, service);
      }

      if (latestService === SERVICES.ROTOSCOPING) {
        handleCanRunInference();
        getCutouts(undefined, selectedShot.id, service).then((result) => {
          setShotCutouts(result);
        });
      }

      if (latestService === SERVICES.SMART_VECTORS) {
        getCutouts(undefined, selectedShot.id, service).then((result) => {
          setShotCutouts(result);
        });
      }
    };

    return () => {
      closeSocket("cutout");
    };
  }, [
    selectedShot?.id,
    refetchLayers,
    handleCanRunInference,
    selectedShot,
    setShotCutouts,
    service,
  ]);

  useEffect(() => {
    if (!selectedShot || !project) return () => {};

    const socket = getSocket("inference-cutout");
    if (!socket) return () => {};

    socket.onopen = () => {
      socket.send(
        getRequestData(ACTION_SUBSCRIBE_INFERENCE_UPDATE, {
          project_id: project.id,
        }),
      );
    };

    socket.onmessage = (event) => {
      getCutouts(undefined, selectedShot.id, service).then((result) => {
        setShotCutouts(result);
      });

      try {
        const { layer_id: updatedLayerId } = JSON.parse(event.data)?.data ?? {};
        if (typeof updatedLayerId === "number") {
          getLayerDetail(updatedLayerId).then((layer) => {
            setLayers((oldLayers) =>
              oldLayers?.map((l) => (l.id === layer.id ? layer : l)),
            );
          });
        }
      } catch {
        // ignore malformed message
      }
    };

    return () => {
      closeSocket("inference-cutout");
    };
  }, [
    project,
    selectedShot,
    project?.id,
    selectedShot?.id,
    service,
    setShotCutouts,
    setLayers,
  ]);

  useEffect(() => {
    if (service === SERVICES.ROTOSCOPING) {
      handleCanRunInference();
    }
  }, [service, handleCanRunInference]);

  useEffect(() => {
    setLayersForExport(
      layers?.filter(
        (layer) =>
          !!layer.predictions.find(
            (prediction) => prediction.status === STATUSES.COMPLETED,
          ) &&
          (!layer.is_autopilot_layer || layer.export_count === 0),
      ) || [],
    );
  }, [layers, setLayersForExport]);

  // Loading selected layer...
  useEffect(() => {
    clearAnnotationData();
    setReviewLayer(null);
    if (layerId) {
      if (service === SERVICES.SMART_VECTORS) {
        setSmartVectorsReviewPane("inference_video");
      } else {
        setSmartVectorsReviewPane(null);
      }
      handleGetCutouts(+layerId).then(() => {
        handleGetReviewLayer(+layerId);
      });
      getLayerDetail(+layerId)
        .then((result) => {
          setSelectedLayer(result);
        })
        .finally(() => {});
    } else {
      setSelectedLayer(null);
      setCutouts([]);
      setSmartVectorsReviewPane(null);
    }
  }, [
    clearAnnotationData,
    handleGetCutouts,
    handleGetReviewLayer,
    layerId,
    service,
    setCutouts,
    setReviewLayer,
    setSelectedLayer,
    setSmartVectorsReviewPane,
  ]);

  useEffect(() => {
    if (service === SERVICES.TRACKING) {
      const grouped: Record<string, LayerCompact[]> = {};
      const shouldGroup = some(layers, (layer) => layer.inference_id !== null);

      if (shouldGroup) grouped["0"] = [];
      layers?.forEach((layer, index) => {
        if (shouldGroup) grouped["0"].push(layer);
        else grouped[index] = [layer];
      });
      Object.values(grouped).forEach((values) => {
        values.sort((a, b) => a.frame_number - b.frame_number);
      });
      setTrackingLayers(grouped);

      const layerGroupsCollapsed: Record<string, boolean> = {};
      Object.keys(grouped).forEach((key) => {
        layerGroupsCollapsed[key] = false;
      });
      setCollapsedLayerGroups(layerGroupsCollapsed);
    }
  }, [layers, service]);

  return (
    <>
      <div className={styles.layersContainer}>
        <div className={styles.layersHeader}>
          <h5>{SERVICE_HEADERS[service]}</h5>

          <div className={styles.actionsContainer}>
            <ToggleButtonGroup
              value={layersViewMode}
              exclusive
              onChange={(e, mode) => {
                if (mode) {
                  toggleViewMode(mode, setLayersViewMode);
                  const newState = Object.keys(collapsedLayerGroups).reduce(
                    (acc, key) => {
                      acc[key] = mode === ViewModes.List;
                      return acc;
                    },
                    {} as Record<string, boolean>,
                  );
                  setCollapsedLayerGroups(newState);
                }
              }}
              aria-label="View Mode"
              size="small"
            >
              <ToggleButton
                value="List"
                title="list"
                style={{ padding: 0, border: 0 }}
              >
                <List
                  className={cx(
                    styles.viewIcon,
                    layersViewMode === "List" && styles.viewIconActive,
                  )}
                />
              </ToggleButton>
              <ToggleButton
                value="Preview"
                title="Preview"
                style={{ padding: 0, border: 0 }}
              >
                <Preview
                  className={cx(
                    styles.viewIcon,
                    layersViewMode === "Preview" && styles.viewIconActive,
                  )}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        <div className={styles.layersBody}>
          <Menu
            id="basic-menu"
            anchorEl={layerAnchorEl}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={!!layerAnchorEl}
            onClose={() => {
              setLayerAnchorEl(null);
              setSelectedLayer(null);
              setCutoutInProgress(null);
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#111113",
                borderRadius: "7px",
                border: "1px solid #CCCCDC1F",
                padding: "8px 6px",
                width: "184px",
                boxShadow: "0px 8px 24px 0px #010409",
              },
            }}
          >
            <MenuItem
              className={styles.menuItem}
              id="LayerRenameBtn"
              onClick={() => {
                setLayerAnchorEl(null);
                setShowRenameLayerModal(true);
              }}
            >
              <RenameIcon style={{ marginRight: "8px" }} />
              Rename
            </MenuItem>
          </Menu>

          {layers &&
            layers.length > 0 &&
            service !== SERVICES.DEPTH_MAP &&
            service !== SERVICES.TRACKING &&
            service !== SERVICES.SMART_VECTORS && (
              <Button
                onClick={handleNewLayer}
                className={styles.newLayer}
                sx={{
                  ".MuiSvgIcon-root": {
                    width: "16px",
                    height: "16px",
                    marginRight: "4px",
                  },
                }}
              >
                <AddIcon />
                {BUTTON_TEXT[service]}
              </Button>
            )}

          <div className={styles.layersScrollCon}>
            <div className={styles.layers}>
              {!layers?.length && (
                <div className={styles.noLayer}>
                  <div className={styles.layersIconCon}>
                    <LayersIcon />
                  </div>
                  <div className={styles.layersHint}>
                    {SERVICE_HINT[service]}
                  </div>
                </div>
              )}
              {service !== SERVICES.TRACKING
                ? layers?.map((l, index) => {
                    const isSmartVectors = service === SERVICES.SMART_VECTORS;
                    const status = l.latest_inference_cutout_status;

                    let guideId = SERVICE_GUIDE[service];
                    let guideTitle = SERVICE_GUIDE_TITLE[service];
                    let guideDescription =
                      service === SERVICES.DEPTH_MAP ||
                      service === SERVICES.SMART_VECTORS
                        ? "Single click to view the output in the player, double click to rename, right click to see more options."
                        : "Click to view in the player, double click to rename this layer, right click to view more options.";
                    let guideOpen =
                      index === layers.length - 1 &&
                      !layerId &&
                      !isAnnotating &&
                      !isInferencing;

                    if (isSmartVectors) {
                      if (status === STATUSES.IN_PROGRESS) {
                        guideId = UserGuides.SmartVectorsGenerating;
                        guideTitle = "Generating Motion Vectors";
                        guideDescription =
                          "Slapshot is generating your Motion Vectors. You can continue with other tasks in the meantime.";
                        guideOpen = !layerId && !isAnnotating;
                      } else if (status === STATUSES.FAILED) {
                        guideId = UserGuides.SmartVectorsError;
                        guideTitle = "Motion Vectors generation failed";
                        guideDescription =
                          "Your Motion Vectors run failed. You can try again, or contact support if the problem persists.";
                        guideOpen = !layerId && !isAnnotating;
                      }
                    }

                    const handleLayerClick = () => {
                      if (isAnnotating) {
                        if (l.id !== selectedLayer?.id) {
                          setSelectedLayer(l);
                          setCutoutInProgress(null);
                        }
                      } else if (selectedShot) {
                        if (isSmartVectors) {
                          setSmartVectorsReviewPane("inference_video");
                        } else {
                          setSmartVectorsReviewPane(null);
                        }
                        handleViewedInference(l);
                        navigate(
                          `/projects/${project.id}/${service}/shots/${selectedShot.id}/layers/${l.id}`,
                        );
                      }
                    };

                    const layerCutouts = shotCutouts.filter(
                      (c) => c.layer_id === l.id,
                    );
                    const supportsCascade =
                      service === SERVICES.ROTOSCOPING &&
                      layersViewMode === ViewModes.Preview &&
                      !l.is_autopilot_layer;
                    const isCascadeExpanded = expandedLayerId === l.id;

                    const isActiveLayer =
                      layerId !== undefined && +layerId === l.id;

                    // Pre-inference "Add more annotations" tip — hover-only
                    // on the cascade header's "+" button (rendered inside
                    // `LayerCascade`).
                    let addAnnotationTip: {
                      id: UserGuides;
                      title: string;
                      description: string;
                    } | null = null;
                    if (
                      supportsCascade &&
                      layerCutouts.length === 1 &&
                      status === null &&
                      !isAnnotating &&
                      !isInferencing
                    ) {
                      addAnnotationTip = {
                        id: UserGuides.AddMoreAnnotations,
                        title: "Add more annotations",
                        description:
                          "You've added your first annotation. Adding more to the same layer can yield even better results. Don't forget to hit the run inference button to see the improvements!",
                      };
                    }

                    // Post-inference review tip — auto-opens on the layer
                    // card via the outer `<Guide>`; overrides `FirstLayer`.
                    if (
                      supportsCascade &&
                      status === STATUSES.COMPLETED &&
                      isActiveLayer &&
                      !isAnnotating &&
                      !isInferencing
                    ) {
                      guideId = UserGuides.AddMoreAnnotationsAfterInference;
                      guideTitle = "Not happy with this output?";
                      guideDescription =
                        "You can try and add more annotations for this layer on more frames. Sometimes it yields better results. Run your inference again once done.";
                      guideOpen = true;
                    }

                    const layerSharedProps = {
                      layer: l,
                      isAnnotating,
                      layerId,
                      layerInputEl,
                      layers,
                      layersViewMode,
                      selectedLayer,
                      setLayerAnchorEl,
                      setLayers,
                      setSelectedLayer,
                      setCutoutInProgress,
                      setShowRenameLayerModal,
                      showRenameLayerModal,
                      handleSingleClick: handleLayerClick,
                      setShowLayerDeleteModal,
                    };

                    let layerEl: React.ReactNode;
                    if (supportsCascade) {
                      layerEl = (
                        <LayerCascade
                          {...layerSharedProps}
                          cutouts={layerCutouts}
                          isExpanded={isCascadeExpanded}
                          onToggle={() =>
                            setExpandedLayerId((prev) =>
                              prev === l.id ? null : l.id,
                            )
                          }
                          addAnnotationTip={addAnnotationTip}
                        />
                      );
                    } else {
                      layerEl = <Layer key={l.id} {...layerSharedProps} />;
                    }

                    return (
                      <Guide
                        id={guideId}
                        key={l.id}
                        open={guideOpen}
                        title={guideTitle}
                        description={guideDescription}
                        placement="left"
                      >
                        <div>{layerEl}</div>
                      </Guide>
                    );
                  })
                : Object.entries(trackingLayers).map(([key, layerGroup]) => (
                    <TrackingLayer
                      key={key}
                      layerGroup={layerGroup}
                      layerId={layerId}
                      setSelectedLayer={setSelectedLayer}
                      setCutoutInProgress={setCutoutInProgress}
                      setLayers={setLayers}
                      handleSingleClick={() => {
                        if (isAnnotating) {
                          if (layerGroup[0].id !== selectedLayer?.id) {
                            setSelectedLayer(layerGroup[0]);
                            setCutoutInProgress(null);
                          }
                        } else if (selectedShot) {
                          layerGroup.forEach((layer) => {
                            handleViewedInference(layer);
                          });
                          navigate(
                            `/projects/${project.id}/${service}/shots/${selectedShot.id}/layers/${layerGroup[0].id}`,
                          );
                        }
                      }}
                    >
                      {layerGroup.map((l) => (
                        <div key={l.id}>
                          <Layer
                            layer={l}
                            key={l.id}
                            isAnnotating={isAnnotating}
                            layerId={layerId}
                            layers={layers}
                            layersViewMode={layersViewMode}
                            selectedLayer={selectedLayer}
                            setLayerAnchorEl={setLayerAnchorEl}
                            setLayers={setLayers}
                            setSelectedLayer={setSelectedLayer}
                            setCutoutInProgress={setCutoutInProgress}
                            handleSingleClick={() => {
                              if (selectedShot) {
                                handleViewedInference(l);
                                navigate(
                                  `/projects/${project.id}/${service}/shots/${selectedShot.id}/layers/${l.id}`,
                                );
                                const newShotCutouts = shotCutouts.filter(
                                  (c) =>
                                    c.layer_id === l.id &&
                                    (!c.inference ||
                                      c.inference.status === null),
                                );
                                if (newShotCutouts.length > 0) {
                                  setTimeout(() => {
                                    window.dispatchEvent(
                                      new CustomEvent(WINDOW_EVENTS.ANNOTATE, {
                                        detail: {
                                          frame: newShotCutouts[0].frame,
                                        },
                                      }),
                                    );
                                  }, 1000);
                                }
                              }
                            }}
                            setShowLayerDeleteModal={setShowLayerDeleteModal}
                          />
                        </div>
                      ))}
                    </TrackingLayer>
                  ))}

              {service === SERVICES.TRACKING &&
                some(
                  layers,
                  (layer) =>
                    layer.latest_inference_cutout_status === STATUSES.COMPLETED,
                ) && <TrackingOutputGuide />}

              {service === SERVICES.SMART_VECTORS &&
                some(
                  layers,
                  (layer) =>
                    layer.latest_inference_cutout_status === STATUSES.COMPLETED,
                ) && <SmartVectorsOutputGuide />}
            </div>
          </div>

          {service === SERVICES.ROTOSCOPING && (layers?.length || 0) > 0 && (
            <Guide
              id={UserGuides.RunInference}
              open={!!newCutouts.length}
              title="Click to Run Inference"
              description="Click here to activate Slapshot's AI and begin prediction of the selected objects."
              placement="top"
            >
              <Button
                disabled={!newCutouts.length || !!throttledTill}
                className={styles.inferenceBtn}
                onClick={() => {
                  if (
                    throttleUsage?.is_inference_throttled &&
                    (throttleUsage.allowed_inferences || 0) < newCutouts.length
                  )
                    setShowThrottleDialog(true);
                  else handleRunInference(newCutouts);
                }}
              >
                <AiIcon />
                <span>
                  {throttleCountdown
                    ? `Wait for ${throttleCountdown}`
                    : "Run Prediction"}
                </span>
              </Button>
            </Guide>
          )}
          {service === SERVICES.SMART_VECTORS && selectedShot && (
            <SmartVectorsGenerate aiIcon={<AiIcon />} />
          )}
          {service === SERVICES.TRACKING && showTrackingPanel && (
            <TrackingSidePanel
              handleClose={() => {
                setShowTrackingPanel(false);
              }}
              setIsTracking={(value) => {
                setIsTracking(value);
              }}
            />
          )}
        </div>
      </div>

      <InferencingBackdrop
        isInferencing={isInferencing}
        heading="Slapshot is predicting your layers..."
        subHeading="You can continue with your other tasks in the meantime."
      />

      <InferencingBackdrop
        isInferencing={isTracking}
        heading="Slapshot is generating your camera track..."
        subHeading="You can continue with your other tasks in the meantime."
      />

      <Dialog
        open={showLayerDeleteModal}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">Delete Layer</h3>
            <button
              onClick={() => {
                setShowLayerDeleteModal(false);
              }}
              type="button"
              className="closeButton"
            >
              <XMark />
            </button>
          </div>
          <div className={styles.confirmationText}>
            <p>
              Are you sure you want to delete this layer&nbsp;
              <b>{selectedLayer?.name}</b>&nbsp;?
            </p>
          </div>
          <div className="modalActions">
            <Button
              type="button"
              className={styles.noBackgroundButton}
              onClick={() => {
                setShowLayerDeleteModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={styles.outlineButtonRed}
              onClick={(e) => {
                e.stopPropagation();
                if (selectedLayer)
                  deleteLayer(selectedLayer.id)
                    .then(() => {
                      if (
                        layerId &&
                        +layerId === selectedLayer.id &&
                        selectedShot
                      )
                        navigate(
                          `${SLAPSHOT_HOME_URL}projects/${project.id}/${service}/shots/${selectedShot.id}`,
                        );
                      setLayers((prevLayers) =>
                        prevLayers?.filter((l) => l.id !== selectedLayer.id),
                      );
                    })
                    .catch((err) => {
                      toast.error(err.data);
                    })
                    .finally(() => {
                      setSelectedLayer(null);
                      setCutoutInProgress(null);
                      setShowLayerDeleteModal(false);
                    });
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>

      {showThrottleDialog && (
        <ThrottlePopup
          layers={shotCutouts
            .filter((c) => newCutouts.includes(c.id))
            .map((c) => {
              const layer = layers?.find((l) => l.id === c.layer_id);
              if (!layer) return null;
              return {
                id: c.id,
                name: layer.name,
                cover_image_url: layer.cover_image_url,
              };
            })
            .filter((l) => !!l)}
          context="Prediction"
          onClose={() => setShowThrottleDialog(false)}
          onSubmit={(cutoutIds) => {
            setShowThrottleDialog(false);
            handleRunInference(cutoutIds);
          }}
        />
      )}
    </>
  );
};

export default Layers;
